import React, { useEffect, useState } from 'react'
import { track } from '@adthlete/shared-frontend'
import { useNavigate, useParams } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'
import SetupShell, { SectionTitle, SetupCard } from '@/components/setup/SetupShell'
import {
  HOW_IT_WORKS,
  deadlineLabel,
  dealTypeTab,
  opportunitiesPath,
  opportunityPay,
} from '@/lib/opportunities'
import { useApplyToOpportunityMutation, useGetOpportunityQuery } from '@/redux/collaborationsApi'

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-gray-600">{children}</p>
)

/**
 * One opportunity and the form to apply: what the brand wants, what the deal pays, how it
 * works, then the athlete's note and (for paid deals) their ask. Sending creates a deal that
 * waits on the brand, and lands on its Deal screen.
 */
const OpportunityApply: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { athlete } = useAuth()
  const { data: o, isLoading, isError } = useGetOpportunityQuery(id, { skip: !id })
  const [apply, { isLoading: sending }] = useApplyToOpportunityMutation()
  const [message, setMessage] = useState('')
  const [ask, setAsk] = useState('')

  useEffect(() => {
    if (o && !ask && o.payout) setAsk(String(Math.round(o.payout)))
  }, [o]) // eslint-disable-line react-hooks/exhaustive-deps

  const backTo = o ? opportunitiesPath(dealTypeTab(o.dealType)) : opportunitiesPath('all')

  if (isLoading || (!o && !isError)) {
    return (
      <SetupShell title="Opportunity" backTo={backTo}>
        <p className="text-sm text-gray-500">Loading…</p>
      </SetupShell>
    )
  }
  if (!o) {
    return (
      <SetupShell title="Opportunity" backTo={backTo}>
        <p className="rounded-2xl border border-dashed border-foreground/15 p-4 text-sm text-gray-600">
          This opportunity is no longer open.
        </p>
      </SetupShell>
    )
  }

  const paid = o.dealType !== 'gifting'
  const due = deadlineLabel(o.deadline)
  const firstName = (athlete?.name ?? '').split(' ')[0]
  const placeholder = `Hi ${o.brand?.name ?? 'there'}, I'm ${firstName || 'an athlete'} and I'd love to work on this because…`

  const send = async () => {
    if (message.trim().length < 10) {
      toast.error('Add a short note', { description: 'A sentence or two about why you fit.' })
      return
    }
    try {
      const res = await apply({
        id: o.id,
        message: message.trim(),
        ask: paid && ask !== '' ? Number(ask) : null,
      }).unwrap()
      track('opportunity_applied', { opportunityId: o.id })
      toast('Sent to the brand', { description: "We'll tell you when they answer." })
      navigate(`/deals/${res.dealId}`, { replace: true })
    } catch (err: any) {
      toast.error("Couldn't send", { description: err?.data?.error ?? 'Try again in a moment.' })
    }
  }

  return (
    <SetupShell
      title={o.brand?.name ?? 'Opportunity'}
      backTo={backTo}
      subtitle={
        <div>
          <SectionTitle>{o.title}</SectionTitle>
          <p className="mt-1 text-sm text-gray-600">
            {o.dealTypeLabel} · {opportunityPay(o)}
            {due ? ` · ${due}` : ''}
          </p>
        </div>
      }
      footer={
        o.applied ? (
          <Button
            variant="lime"
            className="h-12 w-full text-base"
            onClick={() => navigate(`/deals/${o.applied!.dealId}`)}
          >
            Open your deal
          </Button>
        ) : (
          <Button
            variant="lime"
            className="h-12 w-full text-base"
            disabled={sending}
            onClick={send}
          >
            {sending ? 'Sending…' : 'Send offer'}
          </Button>
        )
      }
      footnote={
        o.applied
          ? 'You already applied. The brand will answer on your deal.'
          : 'The brand can accept, counter, or pass. Nothing is final until you both agree.'
      }
    >
      <SetupCard highlight={o.fit >= 80}>
        <div className="flex items-center justify-between gap-3">
          <p className="font-bold text-foreground">{o.fit}% fit</p>
          {o.fitReasons.length ? (
            <p className="text-xs text-gray-600">
              Matches {o.fitReasons.join(', ').toLowerCase()}
            </p>
          ) : (
            <p className="text-xs text-gray-600">Open to everyone</p>
          )}
        </div>
      </SetupCard>

      {o.brief ? (
        <SetupCard>
          <Label>What the brand wants</Label>
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-800">{o.brief}</p>
          {o.instructions ? (
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-600">
              {o.instructions}
            </p>
          ) : null}
        </SetupCard>
      ) : null}

      {o.gifting ? (
        <SetupCard>
          <Label>Bonuses as your post takes off</Label>
          <ul className="grid grid-cols-3 gap-2">
            {o.gifting.bonuses.map((b) => (
              <li
                key={b.views}
                className="rounded-xl border border-foreground/10 px-2 py-2 text-center"
              >
                <span className="block text-xs text-gray-600">
                  {b.views >= 1_000_000 ? `${b.views / 1_000_000}M` : `${b.views / 1000}K`} views
                </span>
                <span className="block font-bold tabular-nums text-foreground">
                  ${b.amount.toLocaleString('en-US')}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-600">
            Bonuses stack: hit 25K views and you've earned both the 10K and 25K bonus. The brand
            can run your post as an ad for {o.gifting.usageDays} days.
          </p>
        </SetupCard>
      ) : null}

      <SetupCard>
        <Label>How it works</Label>
        <ol className="space-y-2">
          <li className="flex items-start gap-3 text-sm text-gray-700">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C0FD72] text-xs font-bold text-black">
              1
            </span>
            <span>You apply, the brand agrees to terms</span>
          </li>
          {HOW_IT_WORKS[o.dealType].map((step, i) => (
            <li key={step} className="flex items-start gap-3 text-sm text-gray-700">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground">
                {i + 2}
              </span>
              <span>{step}</span>
            </li>
          ))}
          <li className="flex items-start gap-3 text-sm text-gray-700">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground">
              {HOW_IT_WORKS[o.dealType].length + 2}
            </span>
            <span>{paid ? 'Athios pays you' : 'Keep the product'}</span>
          </li>
        </ol>
      </SetupCard>

      {o.applied ? (
        <SetupCard>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-[#4d7c0f]">
            <Check className="h-4 w-4" strokeWidth={3} /> Sent to {o.brand?.name ?? 'the brand'}
          </p>
          <p className="mt-1 text-sm text-gray-600">Waiting on the brand to answer.</p>
        </SetupCard>
      ) : (
        <SetupCard>
          <Label>Your note to {o.brand?.name ?? 'the brand'}</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={placeholder}
            rows={4}
            maxLength={1000}
            className="min-h-24 border-border bg-card text-base text-foreground placeholder:text-muted-foreground"
          />
          {paid ? (
            <div className="mt-4">
              <Label>Your ask</Label>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-foreground">$</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={ask}
                  onChange={(e) => setAsk(e.target.value)}
                  className="h-12 border-foreground/15 bg-black/30 text-base text-foreground tabular-nums"
                />
                {o.dealType === 'licensing' ? (
                  <span className="text-sm text-gray-600">a month</span>
                ) : null}
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                The brand offered {opportunityPay(o)}. Ask for more if your numbers back it up.
              </p>
            </div>
          ) : (
            <p className="mt-3 text-xs text-gray-500">
              Gifting deals pay in product. Share your address in Settings so it can ship.
            </p>
          )}
        </SetupCard>
      )}
    </SetupShell>
  )
}

export default OpportunityApply
